import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import {
  Package,
  Truck,
  MapPin,
  CheckCircle2,
  Circle,
  Clock,
} from "lucide-react";
import { OrderFeedbackForm } from "@/components/OrderFeedbackForm";

const statusOrder = ["created", "confirmed", "packed", "shipped", "in_transit", "out_for_delivery", "delivered"];

const OrderTrack = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [feedback, setFeedback] = useState(null);
  useEffect(() => {
    api.get(`/orders/${id}/track`)
      .then((r) => Promise.all([Promise.resolve(r.data), api.get(`/orders/${id}`)]).then(([t, o]) => setData({ ...t, order: o.data })))
      .catch(() => setErr("Order not found"));
  }, [id]);

  useEffect(() => {
    if (!data?.order?.id) return;
    api.get(`/orders/${data.order.id}/feedback-inline`)
      .then((response) => setFeedback(response.data.eligible ? response.data : null))
      .catch(() => setFeedback(null));
  }, [data?.order?.id]);

  if (err) return <div className="text-center py-20 text-[#64748B]">{err}. <Link to="/account?tab=orders" className="text-[#C86D42]">Back to orders</Link></div>;
  if (!data) return <div className="text-center py-20">Loading…</div>;

  const t = data.tracking || {};
  const live = data.live || {};
  const o = data.order;
  const currentStatus = live.status || t.status || o?.order_status || "created";
  const returnRequest = o?.return_request;
  const reversePickup = returnRequest?.reverse_pickup;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8" data-testid="track-page">
      <Link to="/account?tab=orders" className="text-sm text-[#64748B] hover:text-[#C86D42]">← Back to orders</Link>
      <h1 className="font-serif text-4xl font-semibold mt-3">Track your order</h1>
      <div className="text-[#64748B] mt-1 text-sm">Order <span className="font-mono">{o?.id.slice(0, 12)}</span> · {o?.items?.length} items · {inr(o?.totals?.total || 0)}</div>

      <div className="mt-8 bg-white border border-[#E2DCD2] rounded-xl p-6">
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div>
            <div className="text-xs tracking-widest uppercase text-[#8C6D46]">AWB</div>
            <div className="font-mono font-bold text-[#1E2A24]">{t.awb || "—"}</div>
          </div>
          <div>
            <div className="text-xs tracking-widest uppercase text-[#8C6D46]">Courier</div>
            <div className="font-semibold">{t.courier || "Auto-assigned"}</div>
          </div>
          <div>
            <div className="text-xs tracking-widest uppercase text-[#8C6D46]">Estimated Delivery</div>
            <div className="font-semibold">{(live.estimated_delivery || t.estimated_delivery) ? new Date(live.estimated_delivery || t.estimated_delivery).toLocaleDateString("en-IN", { day: "numeric", month: "long" }) : "—"}</div>
          </div>
        </div>

        <div className="border-t border-[#E2DCD2] pt-6">
          <h3 className="font-serif text-xl font-semibold mb-4">Progress</h3>
          <div className="space-y-3">
            {statusOrder.map((s, i) => {
              const done = statusOrder.indexOf(currentStatus) >= i;
              const active = statusOrder.indexOf(currentStatus) === i;
              return (
                <div key={s} className="flex items-center gap-3">
                  {done ? <CheckCircle2 size={20} className="text-[#2D5A47]" /> : <Circle size={20} className="text-[#E2DCD2]" />}
                  <span className={`capitalize ${done ? "text-[#1E2A24] font-medium" : "text-[#64748B]"} ${active ? "text-[#C86D42] font-semibold" : ""}`}>{s.replace(/_/g, " ")}</span>
                </div>
              );
            })}
          </div>
        </div>

        {live.timeline?.length > 0 && (
          <div className="border-t border-[#E2DCD2] pt-6 mt-6">
            <h3 className="font-serif text-xl font-semibold mb-4">Live Timeline</h3>
            <ol className="space-y-4">
              {live.timeline.map((ev, i) => (
                <li key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#C86D42]" />
                    {i < live.timeline.length - 1 && <div className="w-px flex-1 bg-[#E2DCD2] mt-1" />}
                  </div>
                  <div className="pb-2">
                    <div className="text-sm font-semibold">{ev.status}</div>
                    <div className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5"><MapPin size={10} /> {ev.location}</div>
                    <div className="text-xs text-[#8C6D46] mt-0.5"><Clock size={10} className="inline" /> {new Date(ev.date).toLocaleString("en-IN")}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {t.mock && (
          <div className="mt-6 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-lg">
            Shipping is in demo mode — real Shiprocket integration will activate once credentials are added.
          </div>
        )}
      </div>

      {returnRequest && (
        <section
          className="mt-6 border border-[#D9C3A5] bg-[#FDFBF7] p-6"
          data-testid="customer-reverse-pickup-status"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
            Return request
          </p>
          <h2 className="mt-2 font-serif text-2xl font-semibold text-[#1E2A24]">
            {String(returnRequest.status || "requested").replace(/_/g, " ")}
          </h2>
          {reversePickup ? (
            <div className="mt-3 text-sm text-[#52645B]">
              <p>
                Reverse pickup status: {String(
                  reversePickup.status || reversePickup.state,
                ).replace(/_/g, " ")}
              </p>
              {reversePickup.awb && (
                <p className="mt-1 font-mono text-xs text-[#64748B]">
                  AWB {reversePickup.awb}
                  {reversePickup.courier ? ` · ${reversePickup.courier}` : ""}
                </p>
              )}
            </div>
          ) : (
            <p className="mt-3 text-sm text-[#52645B]">
              Your return is being reviewed. Pickup details will appear here once scheduled.
            </p>
          )}
        </section>
      )}

      <div className="mt-6 bg-white border border-[#E2DCD2] rounded-xl p-6">
        <h3 className="font-serif text-xl font-semibold mb-3">Shipping to</h3>
        <div className="text-sm">
          <div className="font-semibold">{o?.address?.full_name} · {o?.address?.mobile}</div>
          <div className="text-[#64748B] mt-1">{o?.address?.line1}, {o?.address?.line2 && `${o.address.line2}, `}{o?.address?.city}, {o?.address?.state} {o?.address?.pincode}</div>
        </div>
      </div>

      {feedback && (
        <section className="mt-6 border border-[#E2DCD2] bg-[#FDFBF7] p-6" data-testid="tracking-inline-feedback">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">Delivered order</p>
          <h2 className="mt-2 font-serif text-2xl font-semibold">Rate your products</h2>
          <p className="mt-2 text-sm text-[#64748B]">Your feedback helps other shoppers and earns Eira Points.</p>
          <div className="mt-6">
            <OrderFeedbackForm
              feedback={feedback}
              testIdPrefix="tracking"
              onSubmitted={() => setFeedback(null)}
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default OrderTrack;
